import React, { Component, ErrorInfo, ReactNode, ComponentType } from 'react';
import {
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
} from '@chakra-ui/react';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Called when the user clicks the reset/try again button */
  onReset?: () => void;
  /** Custom fallback UI to render when an error occurs */
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  /** Title to display in the default error UI */
  title?: string;
  /** Description to display in the default error UI */
  description?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    const { hasError, error } = this.state;
    const {
      children,
      fallback,
      title = 'Something went wrong',
      description = 'An unexpected error occurred. Please try again.',
    } = this.props;

    if (hasError && error) {
      // If a custom fallback is provided, use it
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback(error, this.handleReset);
        }
        return fallback;
      }

      // Default error UI
      return (
        <Alert
          status="error"
          borderRadius="md"
          flexDirection="column"
          alignItems="flex-start"
          p={6}
        >
          <Flex align="center" mb={2}>
            <AlertIcon />
            <AlertTitle>{title}</AlertTitle>
          </Flex>
          <AlertDescription mb={4}>{description}</AlertDescription>
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            onClick={this.handleReset}
          >
            Try Again
          </Button>
        </Alert>
      );
    }

    return children;
  }
}

/**
 * Higher-order component that wraps a component with an ErrorBoundary.
 * The wrapped component will remount when the user clicks "Try Again".
 */
export type WithErrorBoundaryOptions = Omit<ErrorBoundaryProps, 'children' | 'onReset'>;

export function withErrorBoundary<P extends object>(
  WrappedComponent: ComponentType<P>,
  options: WithErrorBoundaryOptions = {},
): React.FC<P> {
  const WithErrorBoundary: React.FC<P> = (props) => {
    const [resetKey, setResetKey] = React.useState(0);

    const handleReset = () => {
      setResetKey((prev) => prev + 1);
    };

    return (
      <ErrorBoundary {...options} onReset={handleReset}>
        <WrappedComponent key={resetKey} {...props} />
      </ErrorBoundary>
    );
  };

  WithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithErrorBoundary;
}

export { ErrorBoundary as default };
